import React, { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ArticleResponse, fetchArticles } from "./article-service";
import { Article } from "./Article";

// Recursive component to render Clauses and SubClauses
interface ClauseProps {
  clauses: Article["Clauses"];
  level?: number;
}

const ClauseParagraph: React.FC<ClauseProps> = ({ clauses, level = 0 }) => {
  return (
    <>
      {clauses?.map((clause) => (
        <div key={level === 0 ? clause.ClauseNo : (clause as any).SubClauseNo} style={{ marginLeft: level * 20, marginTop: 4 }}>
          <p>
            <strong>
              {level === 0 ? clause.ClauseNo : (clause as any).SubClauseNo}.
            </strong>{" "}
            {level === 0 ? clause.ClauseDesc : (clause as any).SubClauseDesc}
          </p>
          {(clause as any).SubClauses && (
            <ClauseParagraph
              clauses={(clause as any).SubClauses}
              level={level + 1}
            />
          )}
        </div>
      ))}
    </>
  );
};



const ArticleGrid: React.FC = () => {
  const [filters, setFilters] = useState({
    ArtNo: "",
    Name: "",
    Status: "",
    ClauseNo: "",
  });
  const [page, setPage] = useState(1);
  const [limit] = useState(5);
  const [sortBy, setSortBy] = useState("ArtNo");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const { data: articleData, isLoading, isFetching, isError, error, refetch } = useQuery<
    ArticleResponse,
    Error
  >({
    queryKey: ["articles", filters, page, limit, sortBy, order],
    queryFn: () => fetchArticles({ ...filters, page, limit, sortBy, order }),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
  });

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const applyFilters = () => {
    setPage(1);
    refetch();
  };

  return (
    <div className="p-6">
      <h1 className="text-xl font-bold mb-4">Articles</h1>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <input
          type="text"
          name="ArtNo"
          placeholder="Filter by ArtNo"
          value={filters.ArtNo}
          onChange={handleFilterChange}
          className="border p-2 rounded bg-white text-black dark:bg-gray-800 dark:text-white dark:border-gray-600"
        />
        <input
          type="text"
          name="Name"
          placeholder="Filter by Name"
          value={filters.Name}
          onChange={handleFilterChange}
          className="border p-2 rounded bg-white text-black dark:bg-gray-800 dark:text-white dark:border-gray-600"
        />
        {/* <select
          name="Status"
          value={filters.Status}
          onChange={handleFilterChange}
          className="border p-2 rounded"
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select> */}
        <input
          type="text"
          name="ClauseNo"
          placeholder="Filter by ClauseNo"
          value={filters.ClauseNo}
          onChange={handleFilterChange}
          className="border p-2 rounded bg-white text-black dark:bg-gray-800 dark:text-white dark:border-gray-600"
        />
      </div>
      <button
        onClick={applyFilters}
        className="bg-blue-600 text-white px-4 py-2 rounded mb-6"
      >
        Apply Filters
      </button>

      {/* Sorting */}
      {/* <div className="flex items-center gap-4 mb-4">
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="border p-2 rounded"
        >
          <option value="ArtNo">Sort by ArtNo</option>
          <option value="Name">Sort by Name</option>
        </select>
        <select
          value={order}
          onChange={(e) => setOrder(e.target.value as "asc" | "desc")}
          className="border p-2 rounded"
        >
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div> */}

      {/* Articles in paragraph form */}
      {isLoading || isFetching ? (
        <p>Loading...</p>
      ) : isError ? (
        <p className="text-red-500">Error: {(error as Error).message}</p>
      ) : (
        <div>
          {articleData?.data.map((article) => (
            <div key={article.ArtNo} className="mb-6 border-b pb-4">
              <h2 className="text-lg font-bold mb-2">
                Article {article.ArtNo}: {article.Name}{" "}
                {article.Status && <span className="text-sm font-normal">({article.Status})</span>}
              </h2>
              <ClauseParagraph clauses={article.Clauses} />
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
        >
          Prev
        </button>
        <span>
          Page {articleData?.currentPage} of {articleData?.totalPages}
        </span>
        <button
          disabled={page === articleData?.totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ArticleGrid;
